import { Cartao, Entrada, EstadoVazio, Etiqueta, RodapePaginacao, TabelaDados } from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'

import { auditoriaApi } from '../../api/catalogos'
import { chaves } from '../../api/chaves'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeBusca } from '../../componentes/icones'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { usePaginacaoCursor } from '../../ganchos/usar-paginacao-cursor'
import { CabecalhoPagina } from '../../layout/layout-app'
import { formatarDataHora } from '../../utilitarios/formatar'

/**
 * Log de auditoria (`GET /auditoria`).
 *
 * Somente leitura, e é o que o contrato oferece — não há como editar nem apagar
 * registro de auditoria. O gestor vê apenas o log da própria unidade; o recorte é do
 * servidor, e a tela não tem parâmetro para ampliá-lo.
 */
export function PaginaAuditoria() {
  const { filtros, definir } = useFiltrosUrl()
  const paginacao = usePaginacaoCursor(50)

  const consultaFiltros = {
    ...filtros,
    limite: paginacao.limite,
    cursor: paginacao.cursor,
  }

  const consulta = useQuery({
    queryKey: chaves.auditoria(consultaFiltros),
    queryFn: ({ signal }) => auditoriaApi.listar(consultaFiltros, signal),
    staleTime: 30_000,
  })

  const itens = consulta.data?.dados ?? []

  return (
    <>
      <CabecalhoPagina
        titulo="Auditoria"
        descricao="Ações sensíveis registradas pelo sistema. Somente leitura, para todos os papéis."
      />

      <Cartao className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Entrada
            value={filtros.acao ?? ''}
            onChange={(evento) => definir({ acao: evento.target.value || undefined })}
            placeholder="Filtrar por ação (ex.: PAPEL_ALTERADO)"
            prefixo={<IconeBusca className="h-4 w-4" />}
            aria-label="Filtrar por ação"
          />
          <Entrada
            type="date"
            value={filtros.de ?? ''}
            onChange={(evento) => definir({ de: evento.target.value || undefined })}
            aria-label="De"
          />
          <Entrada
            type="date"
            value={filtros.ate ?? ''}
            onChange={(evento) => definir({ ate: evento.target.value || undefined })}
            aria-label="Até"
          />
        </div>
      </Cartao>

      <Cartao semPadding>
        <TabelaDados
          colunas={[
            {
              chave: 'criadoEm',
              cabecalho: 'Quando',
              classe: 'whitespace-nowrap',
              celula: (item) => (
                <span className="text-xs tabular-nums text-conteudo-suave">
                  {formatarDataHora(item.criadoEm)}
                </span>
              ),
            },
            {
              chave: 'acao',
              cabecalho: 'Ação',
              celula: (item) => <Etiqueta tom="marca">{item.acao}</Etiqueta>,
            },
            {
              chave: 'autor',
              cabecalho: 'Autor',
              celula: (item) => (
                <span className="text-xs">{item.autor?.nome ?? 'sistema'}</span>
              ),
            },
            {
              chave: 'entidade',
              cabecalho: 'Recurso',
              ocultarAte: 'md',
              celula: (item) => (
                <span className="font-mono text-xs text-conteudo-suave">
                  {item.entidade}
                  {item.entidadeId ? `#${item.entidadeId}` : ''}
                </span>
              ),
            },
            {
              /**
               * "Detalhes" é o par antes/depois, e é o que responde a pergunta que se
               * faz a uma trilha de auditoria: *o que mudou*. Mostrar só um dos lados
               * transformaria "papel: GESTOR" em algo ambíguo — virou gestor, ou
               * deixou de ser? Em ação de criação `valoresAnteriores` é `null`, e aí o
               * bloco "antes" simplesmente não aparece.
               */
              chave: 'valoresNovos',
              cabecalho: 'Detalhes',
              ocultarAte: 'lg',
              celula: (item) =>
                item.valoresAnteriores || item.valoresNovos ? (
                  <details>
                    <summary className="cursor-pointer text-xs text-conteudo-suave hover:text-conteudo">
                      ver
                    </summary>
                    <div className="mt-1 flex flex-col gap-1">
                      {item.valoresAnteriores ? (
                        <pre className="max-w-md overflow-x-auto rounded bg-superficie-2 p-2 text-[0.6875rem]">
                          antes: {JSON.stringify(item.valoresAnteriores, null, 2)}
                        </pre>
                      ) : null}
                      {item.valoresNovos ? (
                        <pre className="max-w-md overflow-x-auto rounded bg-superficie-2 p-2 text-[0.6875rem]">
                          depois: {JSON.stringify(item.valoresNovos, null, 2)}
                        </pre>
                      ) : null}
                    </div>
                  </details>
                ) : (
                  <span className="text-xs text-conteudo-fraco">—</span>
                ),
            },
          ]}
          itens={itens}
          chaveDe={(item) => item.id}
          carregando={consulta.isPending}
          erro={
            consulta.isError ? (
              <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
            ) : undefined
          }
          vazio={<EstadoVazio titulo="Nenhum registro de auditoria no recorte" />}
        />

        <RodapePaginacao
          limite={paginacao.limite}
          onAlterarLimite={paginacao.alterarLimite}
          quantidadeNaPagina={itens.length}
          indiceInicial={itens.length === 0 ? 0 : paginacao.indiceInicial}
          temAnterior={paginacao.temAnterior}
          temProximo={consulta.data?.paginacao.temMais ?? false}
          onAnterior={paginacao.voltar}
          onProximo={() => paginacao.avancar(consulta.data?.paginacao.proximoCursor ?? null)}
        />
      </Cartao>
    </>
  )
}
